import { useScrollToTop } from '@react-navigation/native';
import type { FC } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { ActivityIndicator, Button, Text } from 'react-native-paper';
import Animated, { useAnimatedScrollHandler } from 'react-native-reanimated';

import { MeinOrtChannelSection } from '@/features/mein-ort/components/funkzentrale/MeinOrtChannelSection';
import { MeinOrtCompactPostRow } from '@/features/mein-ort/components/funkzentrale/MeinOrtCompactPostRow';
import { useMeinOrtScroll } from '@/features/mein-ort/components/MeinOrtScrollContext';
import { useMeinOrtTab } from '@/features/mein-ort/components/MeinOrtTabContext';
import { MeinOrtTab } from '@/features/mein-ort/components/meinOrtTypes';
import { useGossipChannelLabel } from '@/features/mein-ort/hooks/useGossipChannelLabel';
import { useMeinOrtChannelTeasers } from '@/features/mein-ort/hooks/useMeinOrtChannelTeasers';
import { PostScreenFeedCard } from '@/features/posts/components/feed/PostScreenCards';
import { HappeningCard } from '@/features/start/components/radio/HappeningCard';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { Refresher } from '@/shared/components/state-view/Refresher';

const makeStyles = () =>
  StyleSheet.create({
    list: { flex: 1 },
    content: {
      flexGrow: 1
    },
    center: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      gap: 16,
      padding: 32
    },
    sectionContent: { paddingHorizontal: 12 },
    events: {
      flex: 1,
      flexDirection: 'column',
      gap: 12,
      paddingHorizontal: 12
    }
  });

export const MeinOrtAlleTab: FC = () => {
  const { scrollY, feedInset } = useMeinOrtScroll();
  const { onTabChange } = useMeinOrtTab();
  const styles = useMemo(() => makeStyles(), []);
  const { t } = useTranslation(LocalizationNameSpace.MY_PLACE, {
    keyPrefix: 'screens.myPlace'
  });
  const plauschLabel = useGossipChannelLabel();

  const teasers = useMeinOrtChannelTeasers(true);

  const { isLoading, isError, refetch, ...channelTeasers } = teasers;

  const listRef = useRef(null);
  useScrollToTop(listRef);

  const [isPullRefreshing, setIsPullRefreshing] = useState(false);

  const onRefresh = useCallback(() => {
    setIsPullRefreshing(true);
    void refetch().finally(() => setIsPullRefreshing(false));
  }, [refetch]);

  const onScroll = useAnimatedScrollHandler({
    onScroll: (event) => {
      'worklet';
      scrollY.value = event.contentOffset.y;
    }
  });

  if (isLoading) {
    return (
      <View style={[styles.center, { paddingTop: feedInset }]}>
        <ActivityIndicator size={48} />
      </View>
    );
  }

  if (isError) {
    return (
      <View style={[styles.center, { paddingTop: feedInset }]}>
        <Text variant="titleMedium">{t('state.error.title')}</Text>
        <Text variant="bodyMedium">{t('state.error.subtitle')}</Text>
        <Button mode="contained-tonal" onPress={refetch}>
          {t('state.error.retry')}
        </Button>
      </View>
    );
  }

  return (
    <Animated.ScrollView
      ref={listRef}
      style={styles.list}
      refreshControl={
        <Refresher
          refreshing={isPullRefreshing}
          onRefresh={onRefresh}
          progressViewOffset={feedInset}
        />
      }
      onScroll={onScroll}
      scrollEventThrottle={16}
      contentContainerStyle={[styles.content, { paddingTop: feedInset }]}
      showsVerticalScrollIndicator={false}
      keyboardShouldPersistTaps="handled"
    >
      {(channelTeasers.news.posts?.length ?? 0) > 0 && (
        <MeinOrtChannelSection
          title={t('tabs.news')}
          sectionKey={MeinOrtTab.NEWS}
          onMore={() => onTabChange(MeinOrtTab.NEWS)}
        >
          <View style={styles.sectionContent}>
            {channelTeasers.news.posts?.map((post) => (
              <PostScreenFeedCard key={post.postId} post={post} />
            ))}
          </View>
        </MeinOrtChannelSection>
      )}
      {(channelTeasers.plausch.posts?.length ?? 0) > 0 && (
        <MeinOrtChannelSection
          title={plauschLabel}
          sectionKey={MeinOrtTab.PLAUSCH}
          onMore={() => onTabChange(MeinOrtTab.PLAUSCH)}
        >
          <View style={styles.sectionContent}>
            {channelTeasers.plausch.posts?.map((post) => (
              <PostScreenFeedCard key={post.postId} post={post} />
            ))}
          </View>
        </MeinOrtChannelSection>
      )}
      {(channelTeasers.markt.posts?.length ?? 0) > 0 && (
        <MeinOrtChannelSection
          title={t('tabs.markt')}
          sectionKey={MeinOrtTab.MARKT}
          onMore={() => onTabChange(MeinOrtTab.MARKT)}
        >
          <MeinOrtCompactPostRow posts={channelTeasers.markt.posts ?? []} />
        </MeinOrtChannelSection>
      )}
      {(channelTeasers.events.posts?.length ?? 0) > 0 && (
        <MeinOrtChannelSection
          title={t('tabs.events')}
          sectionKey={MeinOrtTab.EVENTS}
          onMore={() => onTabChange(MeinOrtTab.EVENTS)}
        >
          <View style={styles.events}>
            {channelTeasers.events.posts?.map((post) => (
              <HappeningCard key={post.postId} {...post} />
            ))}
          </View>
        </MeinOrtChannelSection>
      )}
    </Animated.ScrollView>
  );
};
